import Grid from "@mui/material/Grid2";
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Paper,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import FmeRecordsTable from "./FmeRecordsTable";
import { useSelector } from "react-redux";
import {
  formatDateToDDMMYYYY,
  formatNumber,
} from "../../../../../utils/helpers";
import { ReceiptStatus } from "../ReceiptsTable/ReceiptStatus";
import { useState } from "react";
import FmeUnpairFinalModalTable from "./FmeUnpairFinalModalTable";
import { useDispatch } from "react-redux";
import {
  setUnpairPayload,
  setUnpairPreviewReceipt,
} from "../../../../../features/financeReceiptSlice";
import { ReceiptsEndPointLogic } from "../../../../../logic/Finance/ProviderExpense/ReceiptsEndpointLogic";
import { selectUserData, selectUserToken } from "../../../../../features/userSlice";
import { ParingRateQueryID } from "../helpers/funcs/staticQID";

interface FmeRecordsReceiptProps {
  open: boolean;
  handleClose: () => void;
}

const FmeRecordsReceiptModal = ({
  open,
  handleClose,
}: FmeRecordsReceiptProps) => {
  const { selectedReceipt, unpairPayload, selectedReceiptDateRange } =
    useSelector((state: any) => state.receipts);
  const from_date = selectedReceiptDateRange[0]?.format("YYYY-MM-DD");
  const to_date = selectedReceiptDateRange[1]?.format("YYYY-MM-DD");
  const [isDeleting, setIsDeleting] = useState(false);
  const [openUnpairDialog, setOpenUnpairDialog] = useState(false);
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const response = useSelector(selectUserData);
  const permission = response?.data?.website_permissions?.[0];
  const siteId = permission ? Object.keys(permission)[0] : "";
  const handleUnpairClick = (clickedFme: any) => {
    const receipt = selectedReceipt;

    const previewReceipt = {
      ...receipt,
      fmes: receipt.fmes.filter((fme: any) => fme.fme_id !== clickedFme.fme_id),
    };

    const payload = {
      receipt_shared_uid: receipt.receipt_shared_uid,
      fme_shared_uids: [clickedFme.fme_shared_uid],
    };

    dispatch(setUnpairPreviewReceipt(previewReceipt));
    dispatch(setUnpairPayload(payload));

    setOpenUnpairDialog(true);
    handleClose();
  };

  const handleConfirmDelete = async () => {
    if (!unpairPayload) return;
    try {
      setIsDeleting(true);
      const response =
        await ReceiptsEndPointLogic.deleteFinancialMonthlyEarnings(
          oauthTokenHeader,
          unpairPayload
        );

      if (response && response.status === 200) {
        dispatch(setUnpairPreviewReceipt(null));
        dispatch(setUnpairPayload(null));
        setOpenUnpairDialog(false);

        await ReceiptsEndPointLogic.getAllReceiptData(
          oauthTokenHeader,
          from_date,
          to_date
        );
        await ReceiptsEndPointLogic.getPairingRate(
          oauthTokenHeader,
          ParingRateQueryID,
          from_date,
          to_date,
          siteId
        );
      }
    } catch (error) {
      console.error("Error deleting FME records:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            FME Records connected to this receipt
          </DialogTitle>
          <Grid container mt={0} columnSpacing={2}>
            <Grid size={12}>
              <Box
                className="fmeRecordsWrap"
                display={"flex"}
                alignItems={"flex-start"}
              >
                <Stack>
                  <span>Company</span>
                  <Typography>
                    {selectedReceipt?.finance_company_name || ""}
                  </Typography>
                </Stack>
                <Stack>
                  <span>Date</span>
                  <Typography>
                    {formatDateToDDMMYYYY(selectedReceipt?.receipt_date) || ""}
                  </Typography>
                </Stack>
                <Stack>
                  <span>Receipt Number</span>
                  <Typography>{selectedReceipt?.receipt_id || ""}</Typography>
                </Stack>
                <Stack>
                  <span>Receipt Amount</span>
                  <Box display={"flex"} alignItems={"center"}>
                    <Typography>
                      {formatNumber(selectedReceipt?.receipt_amount || "")}
                    </Typography>
                    <Stack
                      sx={{
                        color: "rgba(0, 0, 0, 0.56)",
                        fontSize: "18px !important",
                        fontWeight: "500",
                        ml: 0.5,
                      }}
                    >
                      {selectedReceipt?.receipt_currency_symbol || ""}
                    </Stack>
                  </Box>
                </Stack>
                <Stack>
                  <span>Receipt Status</span>
                  <Box>
                    <ReceiptStatus status={selectedReceipt?.receipt_status} />
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>
          <FmeRecordsTable onUnpairClick={handleUnpairClick} />
          <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
            <Button color="secondary" variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Dialog>
      <Dialog
        open={openUnpairDialog}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            setOpenUnpairDialog(false);
          }
        }}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Unpair FME Records
          </DialogTitle>

          <FmeUnpairFinalModalTable isUnpairing={isDeleting} />

          <DialogActions sx={{ pt: 2 }}>
            <Button
              variant="text"
              color="secondary"
              onClick={() => setOpenUnpairDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleConfirmDelete}
              disabled={isDeleting || !unpairPayload}
              data-tracker="595"
            >
              {isDeleting ? "SAVING..." : "SAVE CHANGES"}
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>
    </>
  );
};

export default FmeRecordsReceiptModal;
